import { describeApiError } from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { UserX } from 'lucide-react';
import { useState } from 'react';
import { daysLeftLine, erasedOn } from '../activity/signInRemovals';
import { useMarkSignInRemoved, usePendingSignInRemovals } from '../queries';

/**
 * Erasures whose sign-in is still in the tenant (ADR 0012 step 28's follow-up). The portal promises a
 * player their sign-in is removed within 30 days; the app holds no write permission over tenant users, so
 * an administrator deletes the user in the Entra admin centre and marks the erasure here — which is what
 * keeps the promise from resting on anybody's memory. `docs/entra-setup.md` §3.11 is the procedure.
 *
 * <p>Marking is two clicks, the second naming what it asserts: the record says the sign-in is gone, and
 * nothing in Brush can check it.</p>
 */
export function SignInRemovalsPanel() {
  const pending = usePendingSignInRemovals();
  const mark = useMarkSignInRemoved();
  const [confirming, setConfirming] = useState<string | null>(null);

  return (
    <section className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
      <header className="border-b border-ink-800/10 px-6 py-4">
        <h2 className="flex items-center gap-2 font-display font-semibold text-ink-950">
          <UserX className="size-4 text-ink-800/60" /> Sign-ins to remove
        </h2>
        <p className="mt-1 text-xs text-ink-800/60">
          Players who erased their account were told their sign-in is removed within 30 days. Brush cannot
          delete it: in the Entra admin centre, open the external tenant, find the person under Users and
          delete them, then mark the erasure here. Each goes by its replacement id — the person tells you who
          they are, through whatever channel their request arrived by.
        </p>
      </header>

      <div className="space-y-4 p-6">
        <ErrorNotice message={describeApiError(pending.error) ?? describeApiError(mark.error)} />
        {pending.isLoading ? (
          <p className="text-xs text-ink-800/60">Loading…</p>
        ) : (pending.data ?? []).length === 0 ? (
          <p className="text-xs text-ink-800/60">None waiting: every erased account&apos;s sign-in has been removed.</p>
        ) : (
          <ul className="divide-y divide-ink-800/10 rounded-lg border border-ink-800/10">
            {(pending.data ?? []).map((p) => (
              <li key={p.replacementId} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="block truncate font-mono text-xs text-ink-950">{p.replacementId}</span>
                  <span className="text-xs text-ink-800/70">Erased {erasedOn(p.erasedAt)} · </span>
                  <span className={p.flagged ? 'text-xs font-semibold text-red-700' : 'text-xs text-ink-800/70'}>
                    {daysLeftLine(p.daysRemaining)}
                  </span>
                </span>
                {confirming === p.replacementId ? (
                  <span className="flex items-center gap-2 text-xs">
                    <span className="text-ink-800">Deleted from the tenant?</span>
                    <button
                      type="button"
                      disabled={mark.isPending}
                      onClick={() => mark.mutate(p.replacementId, { onSuccess: () => setConfirming(null) })}
                      className="rounded-md bg-felt-700 px-2.5 py-1 font-semibold text-white hover:bg-felt-900 disabled:opacity-40"
                    >
                      Yes, mark removed
                    </button>
                    <button type="button" onClick={() => setConfirming(null)} className="text-ink-800/70 hover:text-ink-950">
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => { mark.reset(); setConfirming(p.replacementId); }}
                    className="text-xs font-medium text-ink-950 underline"
                  >
                    Mark removed
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
